import React, { useEffect, useState } from "react";
import {
  Modal,
  NumberInput,
  Button,
  Flex,
  Divider,
  Textarea,
  Checkbox,
  SimpleGrid,
} from "@mantine/core";
import { TimeInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import dayjs from "dayjs";
import { deleteShiftById } from "../../api/shiftAssignmentApi";
import ClockIcon from "../common/Icons/ClockIcon";
import styled from "styled-components";
import { createNotification } from "../../utils/useNotification";
import { theme } from "../../theme";

interface ShiftModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDay: { start: Date } | null;
  selectedEvent: any;
  setIsEditMode: any;
  isEditMode: boolean;
  getShiftsForShiftPLan: any;
  onAddEvent: any;
  selectedRole: any;
}

const ShiftModal: React.FC<ShiftModalProps> = ({
  isOpen,
  isEditMode,
  onClose,
  setIsEditMode,
  selectedDay,
  selectedEvent,
  onAddEvent,
  getShiftsForShiftPLan,
  selectedRole,
}) => {
  const days = [
    {
      value: "1",
      label: "Pazartesi",
    },
    {
      value: "2",
      label: "Salı",
    },
    {
      value: "3",
      label: "Çarşamba",
    },
    {
      value: "4",
      label: "Perşembe",
    },
    {
      value: "5",
      label: "Cuma",
    },
    {
      value: "6",
      label: "Cumartesi",
    },
    {
      value: "0",
      label: "Pazar",
    },
  ];
  const form = useForm({
    initialValues: {
      startTime: "",
      endTime: "",
      unpaidBreakMinutes: 0,
      employeeCount: 1,
      workDuration: 0,
      notes: "",
      daysOfWeek: [],
    },

    validate: {
      startTime: (value: any) => (!value ? "Başlangıç saati gerekli" : null),
      endTime: (value: any) => (!value ? "Bitiş saati gerekli" : null),
      unpaidBreakMinutes: (value: any) => (value < 0 ? "Negatif olamaz" : null),
      employeeCount: (value: any) => (value <= 0 ? "Minimum 1 olmalı" : null),
      workDuration: (value: any) => (value < 0 ? "Negatif olamaz" : null),
    },
  });

  const [isEditModeOpen, setIsEditModeOpen] = useState(false);

  const deleteShift = async (id: string | number) => {
    try {
      const response = await deleteShiftById({ id });
      const success = response?.success;
      const message = response?.message;
      if (!success) {
        createNotification({
          success: success,
          message: message,
        });
      }

      if (success) {
        await getShiftsForShiftPLan();
        form.reset();
        setIsEditMode(false);
        onClose();
        createNotification({
          success: success,
          message: message,
        });
      }
    } catch (error) {
      console.error("Shift silinirken hata oluştu:", error);
    }
  };

  const handleSubmit = (values: typeof form.values) => {
    const date = dayjs(
      isEditMode ? selectedEvent.startTime : selectedDay?.start
    );
    const dateString = date.format("YYYY-MM-DD");

    const startDateTime = dayjs(`${dateString}T${values.startTime}`);
    const endDateTime = values.workDuration
      ? startDateTime.add(values.workDuration * 60, "minute").toDate()
      : dayjs(`${dateString}T${values.endTime}`).toDate();

    const newEvent = {
      id: selectedEvent?.id,
      startTime: startDateTime.toDate(),
      endTime: endDateTime,
      unpaidBreakMinutes: values.unpaidBreakMinutes,
      count: values.employeeCount,
      title: `${startDateTime.format("HH:mm")} - ${dayjs(endDateTime).format(
        "HH:mm"
      )}`,
      notes: values.notes,
      daysOfWeek: values.daysOfWeek.map((d) => parseInt(d, 10)),
    };

    onAddEvent({
      newEvent: newEvent,
      isEditMode: isEditMode,
    });

    form.reset();
    onClose();
    setIsEditModeOpen(false);
  };

  useEffect(() => {
    if (isOpen && isEditMode && selectedEvent) {
      const start = dayjs(selectedEvent.startTime);
      const end = dayjs(selectedEvent.endTime);
      form.setValues({
        startTime: start.format("HH:mm"),
        endTime: end.format("HH:mm"),
        unpaidBreakMinutes: selectedEvent.unpaidBreakMinutes ?? 0,
        employeeCount: selectedEvent.count ?? 1,
        workDuration: end.diff(start, "minute") / 60,
        notes: selectedEvent.notes ?? "",
      });
      setIsEditModeOpen(false);
    } else if (isOpen && !isEditMode) {
      form.reset();
      setIsEditMode(false);
      setIsEditModeOpen(true);
    }
  }, [isOpen]);

  useEffect(() => {
    const { startTime, workDuration } = form.values;
    if (startTime && workDuration > 0) {
      const baseDate = dayjs("2023-01-01T" + startTime);
      const calculatedEnd = baseDate
        .add(workDuration * 60, "minute")
        .format("HH:mm");
      form.setFieldValue("endTime", calculatedEnd);
    }
  }, [form.values.startTime, form.values.workDuration]);

  return (
    <Modal
      padding={24}
      size={"lg"}
      centered
      withCloseButton={false}
      opened={isOpen}
      onClose={onClose}
    >
      <Flex direction={"column"} gap={16}>
        <Flex direction={"column"} gap={12}>
          <ModalTitle>
            {dayjs(
              isEditMode ? selectedEvent.startTime : selectedDay?.start
            ).format("dddd, MMMM DD")}
          </ModalTitle>
          <div>{selectedRole?.label}</div>
        </Flex>
        <Divider color="#D1D5DB" />
        <form id="shift-form" onSubmit={form.onSubmit(handleSubmit)}>
          <Flex gap={12} w={"100%"}>
            <TimeInput
              disabled={!isEditModeOpen}
              w={"100%"}
              label="Mesai Başlangıç Saati"
              {...form.getInputProps("startTime")}
              rightSection={<ClockIcon size={24} />}
            />
            <TimeInput
              w={"100%"}
              disabled={!isEditModeOpen}
              label="Mesai Bitiş Saati"
              {...form.getInputProps("endTime")}
              readOnly
              rightSection={<ClockIcon size={24} />}
            />
          </Flex>
          <NumberInput
            disabled={!isEditModeOpen}
            hideControls
            label="Çalışma Süresi (saat)"
            placeholder="Mesai bitiş saatinin hesaplanması için lütfen çalışma süresi giriniz."
            step={0.5}
            min={0}
            {...form.getInputProps("workDuration")}
            mt="sm"
          />
          <NumberInput
            disabled={!isEditModeOpen}
            hideControls
            label="Mola Süresi (dakika)"
            {...form.getInputProps("unpaidBreakMinutes")}
            mt="sm"
          />
          <NumberInput
            disabled={!isEditModeOpen}
            hideControls
            label="Çalışan Sayısı"
            {...form.getInputProps("employeeCount")}
            mt="sm"
          />
          <StyledTextArea
            disabled={!isEditModeOpen}
            label="Notlar"
            placeholder="Vardiya için not girebilirsiniz."
            mt="sm"
            autosize
            minRows={2}
            maxRows={2}
            {...form.getInputProps("notes")}
          />

          {!isEditMode && (
            <Checkbox.Group
              label="Tekrarlansın mı? (Haftanın günlerini seçin)"
              description="Seçili günlerde bu vardiya ay sonuna kadar tekrar edilecek."
              descriptionProps={{
                mb: "md",
              }}
              {...form.getInputProps("daysOfWeek")}
              mt="sm"
            >
              <SimpleGrid cols={4} spacing="md">
                {days.map((day: any) => (
                  <Checkbox
                    color={theme.colors.brand.normal}
                    value={day.value}
                    label={day.label}
                    disabled={!isEditModeOpen}
                  />
                ))}
              </SimpleGrid>
            </Checkbox.Group>
          )}
        </form>
        <Flex gap={12} mt="md">
          {isEditMode && (
            <DeleteButton
              w={"100%"}
              onClick={() => deleteShift(selectedEvent?.id)}
            >
              Sil
            </DeleteButton>
          )}
          {isEditModeOpen ? (
            <SubmitButton w={"100%"} type="submit" form="shift-form">
              {isEditMode ? "Güncelle" : "Kaydet"}
            </SubmitButton>
          ) : (
            <EditButton
              type="button"
              onClick={() => setIsEditModeOpen(true)}
              w={"100%"}
            >
              Düzenle
            </EditButton>
          )}
        </Flex>
      </Flex>
    </Modal>
  );
};

export default ShiftModal;

const ModalTitle = styled.div`
  font-weight: 600;
  font-size: 24px;
  line-height: 24px;
  color: #111827;
`;

const SubmitButton = styled(Button)<any>`
  height: 44px;
  border-radius: 8px;
  gap: 12px;
  padding: 11px 20px;
  background-color: #5c26ff;
  &:hover {
    background-color: #5c26ff;
  }
`;

const DeleteButton = styled(Button)<any>`
  height: 44px;
  border-radius: 8px;
  gap: 12px;
  padding: 11px 20px;
  background-color: #ee4549;
  &:hover {
    background-color: #ee4549;
  }
`;

const EditButton = styled(Button)<any>`
  height: 44px;
  border-radius: 8px;
  gap: 12px;
  padding: 11px 20px;
  background-color: #5c26ff;
  &:hover {
    background-color: #5c26ff;
  }
`;

const StyledTextArea = styled(Textarea)`
  .mantine-Textarea-input {
    padding: 5px;
  }
`;
